/* eslint-disable max-len */
import {
  ActionSection,
  ExpandCloseSection,
  Icon,
  LogoIcon,
  MacHeader,
  NavigateArea,
  PlusIcon,
  TabCloseIcon,
  Tabs,
  TabsBarSection,
  TabsName,
  WindowsHeaderIcon
} from './styles';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import BackForwardIcon from '../windowHeader/backForwardIcon';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import history from '../../history';
import { useSelector } from 'react-redux';
import { RootReducerInterface } from '../../interfaces/RootReducerInterface';
import { useDispatch } from 'react-redux';
import { setCurrentChatGroup } from '../../actions/chatMessageActions';
import router from '../../routes/routes';
import { isElectronApp, isIpad } from '../../helpers/common';
import UserPreferenceSingleton from '../../helpers/userPreferenceSingleton';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { MAX_TABS } from '../../mobx/stores/tabsStore';

function TabsBar() {
  const { location } = history;
  const dispatch = useDispatch();

  //use selector state variables
  const stateSelector = useSelector((state: RootReducerInterface) => state);
  const { chatMessage: chatSelector } = stateSelector || {};
  const { chatGroups } = chatSelector || {};

  //Mobx store variables
  const { workspaceStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { tabsStore } = workspace || {};
  const shouldUpdateTab = useRef(true);

  //mobx store variables
  const { appStore } = rootStore;
  const { isNavbarShow } = appStore || {};

  // Others
  const isReportPage = location.pathname.startsWith('/report');
  const isSettingsPage = location.pathname.startsWith('/settings');

  const onChangeMessageGroup = useCallback(
    async (groupId: string | null) => {
      const currentGroup = chatGroups?.find((x: any) => x?.id === groupId) || { id: '', name: '', type: 0 };
      dispatch(setCurrentChatGroup(groupId ? currentGroup : chatGroups[0]));
    },
    [chatGroups, dispatch]
  );

  const excludeTabsRoutes = useMemo(
    () => [
      ...router.filter((route) => route && !route.private).map((route) => route!.path),
      '/select-workspace',
      '/onboarding',
      '/membership'
    ],
    []
  );
  const excludeTabs = useMemo(() => {
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
    return excludeTabsRoutes.some((x) => !userDetails?.id || location.pathname?.includes(x));
  }, [excludeTabsRoutes, location.pathname]);

  useEffect(() => {
    if (excludeTabs || !isElectronApp()) return;
    if (!shouldUpdateTab.current) {
      shouldUpdateTab.current = true;
      return;
    }

    if (tabsStore) {
      tabsStore.onLocationChange();
    }
  }, [excludeTabs, location, tabsStore]);

  const onClickMinimize = useCallback(() => {
    if (window && (window as any).electronApi) (window as any).electronApi.windowAction('minimize'); // close window
  }, []);

  const onClickFullScreen = useCallback(() => {
    if (window && (window as any).electronApi) (window as any).electronApi.windowAction('fullscreen'); // close window
    // window.electronApi.windowAction(‘exit-fullscreen‘) // exit fullscreen
  }, []);

  const onClickClose = useCallback(() => {
    if (window && (window as any).electronApi) (window as any).electronApi.windowAction('close'); // close window
  }, []);

  const onClickLogo = useCallback(() => {
    history.push('/');
  }, []);

  const onClickSideBarToddle = useCallback((value: boolean) => {
    rootStore.appStore.setIsNavbarShow(value);
  }, []);

  if (!tabsStore) return <></>;

  const { tabs, selectedTabId } = tabsStore;

  return !excludeTabs && (isElectronApp() || isIpad()) ? (
    <MacHeader className='app-header'>
      <ActionSection
        className={`back-forward-button ${
          isReportPage ? 'report-page' : isSettingsPage ? 'settings-page' : !isNavbarShow ? 'ipad-header' : ''
        }`}>
        <WindowsHeaderIcon onClick={() => onClickSideBarToddle(!isNavbarShow)} className='expand-sidebar-icon-mac'>
          <SVGIcon name='expand-sidebar-icon' width='16' height='16' viewBox='16' fill='var(--text-secondary)' />
        </WindowsHeaderIcon>
        <NavigateArea className='navigate-area'>
          <LogoIcon onClick={onClickLogo} className='logo-icon'>
            <SVGIcon name='teamcamp-logo' width='24' height='24' viewBox='24' />
          </LogoIcon>
          <WindowsHeaderIcon onClick={() => onClickSideBarToddle(!isNavbarShow)}>
            <SVGIcon name='expand-sidebar-icon' width='16' height='16' viewBox='16' fill='var(--text-secondary)' />
          </WindowsHeaderIcon>
        </NavigateArea>
        <BackForwardIcon />
      </ActionSection>
      <TabsBarSection
        className={`tabsbar-section ${isReportPage ? 'report-page' : isSettingsPage ? 'settings-page' : ''}`}>
        {tabs.map((tab) => (
          <Tabs
            key={tab.id}
            isActive={selectedTabId === tab.id}
            onClick={() => tabsStore.handleTabClick(tab, onChangeMessageGroup)}
            isWithoutbg={tabs.length <= 1}>
            <TabsName>
              <Icon>
                <SVGIcon
                  name={tab.icon}
                  width='18'
                  height='18'
                  viewBox='0 0 18 18'
                  className='fill-color'
                  {...tab.iconProps}
                />
              </Icon>
              <p>{tab.label}</p>
            </TabsName>
            {tabs.length > 1 && (
              <TabCloseIcon
                onClick={(event) => {
                  event.stopPropagation();
                  tabsStore.removeTab(tab.id);
                }}
                className='tabclose-icon'>
                <SVGIcon
                  name='app-close-icon'
                  width='14'
                  height='14'
                  viewBox='0 0 16 16'
                  fill='var(--text-secondary)'
                />
              </TabCloseIcon>
            )}
          </Tabs>
        ))}
        <PlusIcon onClick={() => tabsStore.addTab()} isDisabled={tabs.length >= MAX_TABS}>
          <SVGIcon name='plus-icon' width='18' height='18' viewBox='0 0 18 18' fill='var(--text-secondary)' />
        </PlusIcon>
        <ExpandCloseSection className='expandclose-section'>
          <WindowsHeaderIcon onClick={onClickMinimize}>
            <SVGIcon name='minimize-icon' width='16' height='16' viewBox='0 0 18 18' fill='var(--text-secondary)' />
          </WindowsHeaderIcon>
          <WindowsHeaderIcon onClick={onClickFullScreen}>
            <SVGIcon name='expand-icon' width='16' height='16' viewBox='0 0 18 18' fill='var(--text-secondary)' />
          </WindowsHeaderIcon>
          <WindowsHeaderIcon onClick={onClickClose}>
            <SVGIcon name='app-close-icon' width='16' height='16' viewBox='0 0 18 18' fill='var(--text-secondary)' />
          </WindowsHeaderIcon>
        </ExpandCloseSection>
      </TabsBarSection>
    </MacHeader>
  ) : (
    <></>
  );
}

export default observer(TabsBar);
